import { zIndex } from 'constants/index';
import React, { useEffect, useState, useCallback } from 'react';
import { css } from '@landr/styled-system/css';
import { Modal, Popper, useDebouncedCallback } from '@landr/maestro';
import { useSearchBarContext } from 'components/Filters/context/SearchBarContext/SearchBarContext';

const modalStyles = css({
    margin: 0,
    color: 'unset',
    maxWidth: '100%',
    alignSelf: 'flex-start',
    padding: '0',
    width: '100%',
    borderRadius: 'none',
    height: '100%',
    position: 'fixed',
    top: '0px',
});

const modalContentStyles = css({
    backgroundColor: 'surface.neutral.medium',
    borderRadius: 'none',
    minHeight: '100%',
    height: '100%',
});

export const _SuggestionsWrapper = ({
    children,
    isModal,
    anchorRef,
    isSuggestionsOpen,
    dynamicSearchBarWidth,
}: {
    children: React.ReactNode;
    anchorRef: React.RefObject<HTMLDivElement>;
    isModal: boolean;
    isSuggestionsOpen: boolean;
    dynamicSearchBarWidth: number;
}) => (
    <>
        {isModal ? (
            <Modal
                isOpen={isSuggestionsOpen}
                size="full"
                translate="no"
                minHeight="100%"
                scrollBehavior="inside"
                className={modalStyles}
            >
                <Modal.Content className={modalContentStyles}>
                    {children}
                </Modal.Content>
            </Modal>
        ) : (
            <Popper
                translate="no"
                zIndex={zIndex.searchBar}
                isOpen={isSuggestionsOpen}
                anchorRef={anchorRef}
                backgroundColor="surface.neutral.medium"
                borderBottomLeftRadius="3xl"
                borderBottomRightRadius="3xl"
                overflow="hidden"
                style={{
                    ...({
                        '--dynamic-search-bar-width': `${dynamicSearchBarWidth}px`,
                    } as React.CSSProperties),
                    width: `var(--dynamic-search-bar-width)`,
                }}
                popperOptions={{
                    placement: 'bottom',
                    modifiers: [
                        {
                            name: 'offset',
                            options: {
                                offset: [0, 0],
                            },
                        },
                        {
                            name: 'flip',
                            enabled: false,
                        },
                    ],
                }}
            >
                {children}
            </Popper>
        )}
    </>
);
interface SuggestionsWrapperContainerProps {
    children: React.ReactNode;
    anchorRef: React.RefObject<HTMLDivElement>;
    isModal: boolean;
}

const SuggestionsWrapper: React.FC<SuggestionsWrapperContainerProps> = ({
    children,
    anchorRef,
    isModal,
}) => {
    const { isSuggestionsOpen } = useSearchBarContext();
    const [dynamicSearchBarWidth, setDynamicSearchBarWidth] =
        useState<number>(500);

    // Scroll to top when modal opens
    useEffect(() => {
        if (isModal && isSuggestionsOpen) {
            const timeoutId = setTimeout(() => {
                // Use the most compatible scroll method for all devices
                document.documentElement.scrollTop = 0;
                document.body.scrollTop = 0; // For older Safari
            }, 100);

            return () => clearTimeout(timeoutId);
        }
    }, [isModal, isSuggestionsOpen]);

    const updateSearchBarWidth = useCallback(() => {
        if (anchorRef.current) {
            const { width } = anchorRef.current.getBoundingClientRect();

            setDynamicSearchBarWidth(width);
        }
    }, [anchorRef]);

    const debouncedUpdateWidth = useDebouncedCallback<() => void>(
        updateSearchBarWidth,
        200,
    );

    useEffect(() => {
        if (!anchorRef.current) return;

        // Initial width calculation
        updateSearchBarWidth();

        // Create ResizeObserver
        const resizeObserver = new ResizeObserver(debouncedUpdateWidth);

        resizeObserver.observe(anchorRef.current);

        return () => {
            resizeObserver.disconnect();
        };
    }, [debouncedUpdateWidth, updateSearchBarWidth, anchorRef]);

    return (
        <_SuggestionsWrapper
            isModal={isModal}
            anchorRef={anchorRef}
            isSuggestionsOpen={isSuggestionsOpen}
            dynamicSearchBarWidth={dynamicSearchBarWidth}
        >
            {children}
        </_SuggestionsWrapper>
    );
};

export default SuggestionsWrapper;
